import React, { useEffect, useMemo, useState } from "react";
import {
    CheckCircle2,
    Eye,
    MessageCircle,
    RefreshCw,
    Save,
    Search,
} from "lucide-react";
import styles from "../../CRM/CRM.module.css";
import { fetchLeads, updateLead } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const STATUS_OPTIONS = [
    { value: "a_contatar", label: "A contatar" },
    { value: "email_enviado", label: "E-mail enviado" },
    { value: "respondeu", label: "Respondeu" },
    { value: "follow_up", label: "Follow-up" },
    { value: "novo", label: "Novo" },
    { value: "em_atendimento", label: "Em atendimento" },
    { value: "proposta_enviada", label: "Proposta enviada" },
    { value: "convertido", label: "Convertido" },
    { value: "perdido", label: "Perdido" },
];

const MOTIVO_LABELS = {
    saas: "Ideia SaaS",
    produto: "Produto HL",
    outro: "Outro",
};

const ORIGEM_LABELS = {
    site: "Site",
    prospeccao: "Prospecção",
};

function formatDate(value) {
    if (!value) return "-";
    const normalized = value.endsWith("Z") ? value : `${value.replace("+00:00", "")}Z`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
    }).format(date);
}

function statusLabel(value) {
    return STATUS_OPTIONS.find((item) => item.value === value)?.label || value;
}

function leadSummary(lead) {
    if (lead.origem === "prospeccao") {
        return lead.necessidade || lead.mensagem || lead.empresa || "Prospecção";
    }
    if (lead.necessidade || lead.momento) {
        return [lead.momento, lead.necessidade].filter(Boolean).join(" - ");
    }
    if (lead.motivo === "saas") return lead.ideia || "Projeto SaaS";
    if (lead.motivo === "produto") return lead.produto || "Interesse em produto";
    return lead.mensagem_outro || "Contato geral";
}

function Field({ label, value }) {
    if (!value) return null;
    return (
        <div className={styles.field}>
            <span>{label}</span>
            <strong>{value}</strong>
        </div>
    );
}

export default function PainelCRM() {
    const { password } = useAuth();
    const [leads, setLeads] = useState([]);
    const [counts, setCounts] = useState({});
    const [selectedLead, setSelectedLead] = useState(null);
    const [filters, setFilters] = useState({ q: "", status: "", motivo: "", origem: "" });
    const [statusDraft, setStatusDraft] = useState("novo");
    const [notesDraft, setNotesDraft] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const totalLeads = useMemo(
        () => Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0),
        [counts]
    );

    const selectedId = selectedLead?.id;

    const loadLeads = () => {
        if (!password) return;
        setIsLoading(true);
        setMessage({ type: "", text: "" });

        fetchLeads(password, filters)
            .then((data) => {
                const nextLeads = data.leads || [];
                setLeads(nextLeads);
                setCounts(data.counts || {});

                const current = nextLeads.find((lead) => lead.id === selectedId);
                const nextSelected = current || nextLeads[0] || null;
                setSelectedLead(nextSelected);
                setStatusDraft(nextSelected?.status || "novo");
                setNotesDraft(nextSelected?.observacoes || "");
            })
            .catch((error) => {
                setMessage({ type: "error", text: error.message || "Não foi possível carregar os leads." });
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        loadLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.q, filters.status, filters.motivo, filters.origem]);

    const selectLead = (lead) => {
        setSelectedLead(lead);
        setStatusDraft(lead.status || "novo");
        setNotesDraft(lead.observacoes || "");
        setMessage({ type: "", text: "" });
    };

    const saveLead = () => {
        if (!selectedLead) return;
        setIsSaving(true);
        setMessage({ type: "", text: "" });

        updateLead(password, selectedLead.id, {
            status: statusDraft,
            observacoes: notesDraft,
        })
            .then((data) => {
                const updated = data.lead;
                setSelectedLead(updated);
                setLeads((items) => items.map((item) => (item.id === updated.id ? updated : item)));
                setMessage({ type: "success", text: "Lead atualizado." });
                loadLeads();
            })
            .catch((error) => {
                setMessage({ type: "error", text: error.message || "Não foi possível atualizar o lead." });
            })
            .finally(() => setIsSaving(false));
    };

    return (
        <main className={styles.page}>
            <header className={styles.topbar}>
                <div>
                    <span>CRM HL Software</span>
                    <h1>Leads recebidos</h1>
                </div>
                <div className={styles.topbarActions}>
                    <button type="button" onClick={loadLeads} disabled={isLoading}>
                        <RefreshCw size={18} aria-hidden="true" />
                        Atualizar
                    </button>
                </div>
            </header>

            <section className={styles.summaryGrid} aria-label="Resumo dos leads">
                <div className={styles.metric}>
                    <span>Total</span>
                    <strong>{totalLeads}</strong>
                </div>
                {STATUS_OPTIONS.map((status) => (
                    <div className={styles.metric} key={status.value}>
                        <span>{status.label}</span>
                        <strong>{counts[status.value] || 0}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.filters} aria-label="Filtros">
                <div className={styles.searchBox}>
                    <Search size={18} aria-hidden="true" />
                    <input
                        value={filters.q}
                        onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
                        placeholder="Buscar por nome, e-mail, telefone ou cidade"
                    />
                </div>
                <select
                    value={filters.status}
                    onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
                    aria-label="Filtrar por status"
                >
                    <option value="">Todos os status</option>
                    {STATUS_OPTIONS.map((status) => (
                        <option key={status.value} value={status.value}>
                            {status.label}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.origem}
                    onChange={(event) => setFilters((current) => ({ ...current, origem: event.target.value }))}
                    aria-label="Filtrar por origem"
                >
                    <option value="">Todas as origens</option>
                    <option value="site">Site</option>
                    <option value="prospeccao">Prospecção</option>
                </select>
                <select
                    value={filters.motivo}
                    onChange={(event) => setFilters((current) => ({ ...current, motivo: event.target.value }))}
                    aria-label="Filtrar por motivo"
                >
                    <option value="">Todos os motivos</option>
                    <option value="saas">Ideia SaaS</option>
                    <option value="produto">Produto HL</option>
                    <option value="outro">Outro</option>
                </select>
            </section>

            {message.text && (
                <p className={`${styles.message} ${message.type === "success" ? styles.successMessage : styles.errorMessage}`}>
                    {message.text}
                </p>
            )}

            <section className={styles.workspace}>
                <aside className={styles.listPanel} aria-label="Lista de leads">
                    <div className={styles.panelHeader}>
                        <h2>Contatos</h2>
                        <span>{isLoading ? "Carregando..." : `${leads.length} exibidos`}</span>
                    </div>

                    <div className={styles.leadList}>
                        {leads.length === 0 && !isLoading && (
                            <div className={styles.emptyState}>Nenhum lead encontrado.</div>
                        )}

                        {leads.map((lead) => (
                            <button
                                type="button"
                                key={lead.id}
                                className={`${styles.leadItem} ${selectedLead?.id === lead.id ? styles.activeLead : ""}`}
                                onClick={() => selectLead(lead)}
                            >
                                <div>
                                    <strong>{lead.nome}</strong>
                                    <span className={`${styles.origemBadge} ${styles[`origem_${lead.origem}`] || ""}`}>
                                        {ORIGEM_LABELS[lead.origem] || lead.origem || "Site"}
                                    </span>
                                </div>
                                <p>{leadSummary(lead)}</p>
                                <footer>
                                    <span className={`${styles.statusPill} ${styles[`status_${lead.status}`]}`}>
                                        {statusLabel(lead.status)}
                                    </span>
                                    <small>{formatDate(lead.created_at)}</small>
                                </footer>
                            </button>
                        ))}
                    </div>
                </aside>

                <section className={styles.detailPanel} aria-label="Detalhes do lead">
                    {!selectedLead ? (
                        <div className={styles.emptyDetail}>
                            <Eye size={28} aria-hidden="true" />
                            <p>Selecione um lead para visualizar os detalhes.</p>
                        </div>
                    ) : (
                        <>
                            <div className={styles.detailHeader}>
                                <div>
                                    <span>Lead #{selectedLead.id}</span>
                                    <h2>{selectedLead.nome}</h2>
                                    <p>{selectedLead.email}</p>
                                </div>
                                {selectedLead.whatsapp_url && (
                                    <a href={selectedLead.whatsapp_url} target="_blank" rel="noreferrer">
                                        <MessageCircle size={18} aria-hidden="true" />
                                        WhatsApp
                                    </a>
                                )}
                            </div>

                            <div className={styles.fieldsGrid}>
                                <Field label="Origem" value={ORIGEM_LABELS[selectedLead.origem] || selectedLead.origem} />
                                <Field label="Empresa" value={selectedLead.empresa} />
                                <Field label="Telefone" value={selectedLead.telefone} />
                                <Field label="Cidade" value={selectedLead.cidade} />
                                <Field label="Estado" value={selectedLead.estado} />
                                <Field label="Motivo" value={MOTIVO_LABELS[selectedLead.motivo] || selectedLead.motivo} />
                                <Field label="Necessidade" value={selectedLead.necessidade} />
                                <Field label="Momento" value={selectedLead.momento} />
                                <Field label="Criado em" value={formatDate(selectedLead.created_at)} />
                                <Field label="Atualizado em" value={formatDate(selectedLead.updated_at)} />
                                <Field label="Mensagem" value={selectedLead.mensagem} />
                                <Field label="Ideia" value={selectedLead.ideia} />
                                <Field label="Validou com público" value={selectedLead.validou} />
                                <Field label="Possui empresa/CNPJ" value={selectedLead.possui_empresa} />
                                <Field label="Orçamento estimado" value={selectedLead.orcamento} />
                                <Field label="Produto" value={selectedLead.produto} />
                                <Field label="Negócio em funcionamento" value={selectedLead.negocio_funciona} />
                                <Field label="Usuários pretendidos" value={selectedLead.qtd_clientes} />
                                <Field label="Mensagem antiga" value={selectedLead.mensagem_outro} />
                            </div>

                            <div className={styles.editor}>
                                <label htmlFor="lead-status">Status</label>
                                <select
                                    id="lead-status"
                                    value={statusDraft}
                                    onChange={(event) => setStatusDraft(event.target.value)}
                                >
                                    {STATUS_OPTIONS.map((status) => (
                                        <option key={status.value} value={status.value}>
                                            {status.label}
                                        </option>
                                    ))}
                                </select>

                                <label htmlFor="lead-notes">Observações internas</label>
                                <textarea
                                    id="lead-notes"
                                    value={notesDraft}
                                    onChange={(event) => setNotesDraft(event.target.value)}
                                    placeholder="Registre combinados, próximos passos ou contexto do atendimento."
                                />

                                <button type="button" onClick={saveLead} disabled={isSaving}>
                                    {isSaving ? (
                                        "Salvando..."
                                    ) : (
                                        <>
                                            <Save size={18} aria-hidden="true" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                                {message.type === "success" && (
                                    <span className={styles.savedHint}>
                                        <CheckCircle2 size={16} aria-hidden="true" />
                                        Alterações salvas
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </section>
            </section>
        </main>
    );
}
